import { useLanguage } from "../../../hooks/useLanguage";
import { useProduct } from "../../../hooks/useProduct";
import { X } from "lucide-react";

const ourValuesPhoto = `${
  import.meta.env.VITE_CLOUDFRONT_URL
}/PHOTO-VALEURS.png`;

const valuesContent = {
  en: {
    title: "Our Values",
    values: [
      {
        emoji: "🎯",
        title: "REAL > FAKE",
        description:
          "We don't mess around with authenticity. Every Korean Street recipe comes straight from Seoul's streets. No shortcuts, no imitations. Real fermented kimchi, real gochujang, real flavors that hit hard.",
      },
      {
        emoji: "🔥",
        title: "BOLD IS BEAUTIFUL",
        description:
          "Too spicy for some? Perfect. Too intense for others? Even better. We own our bold choices because Seoul doesn't do half-measures. Neither do we.",
      },
      {
        emoji: "⚡",
        title: "FAST BUT NEVER CHEAP",
        description:
          "Speed ≠ sacrifice. We're revolutionizing fast food with products that respect Korean tradition. 3 minutes for a real Seoul bowl - it's possible AND it's our promise.",
      },
      {
        emoji: "🌍",
        title: "SEOUL TO THE WORLD",
        description:
          "Mission: democratize real Korean food culture. Everywhere. Every Korean Street bowl is a direct passport to Seoul, no visa required.",
      },
      {
        emoji: "💚",
        title: "RESPECT THE CRAFT",
        description:
          "We innovate, but always while honoring Korea's culinary heritage.",
      },
      {
        emoji: "🚀",
        title: "OBSESSED WITH BETTER",
        description:
          "Continuous improvement. Every recipe, every detail can be optimized. Excellence isn't an accident, it's a daily obsession.",
      },
      {
        emoji: "💥",
        title: "COMMUNITY DRIVEN",
        description:
          "Our fans aren't customers, they're Seoul ambassadors. Their feedback makes us evolve, their passion inspires us, their energy pushes us forward.",
      },
      {
        emoji: "🎨",
        title: "CREATIVITY UNLEASHED",
        description:
          "Tradition + Innovation = Korean Street Magic. We respect the codes while reinventing them. Spicy Carbonara? Nobody saw it coming. And that's exactly the Korean Street spirit.",
      },
    ],
    footer: "No compromise. Just Seoul spirit. ✨",
  },
  fr: {
    title: "Nos Valeurs",
    values: [
      {
        emoji: "🎯",
        title: "REAL > FAKE",
        description:
          "On ne joue pas avec l'authenticité. Chaque recette Korean Street vient directement des rues de Seoul. Pas de raccourcis, pas d'imitations. Du vrai kimchi fermenté, du vrai gochujang, de vraies saveurs qui claquent.",
      },
      {
        emoji: "🔥",
        title: "BOLD IS BEAUTIFUL",
        description:
          "Trop épicé pour certains ? Perfect. Trop intense pour d'autres ? Encore mieux. On assume nos choix audacieux parce que Seoul ne fait pas dans la demi-mesure. Et nous non plus.",
      },
      {
        emoji: "⚡",
        title: "FAST BUT NEVER CHEAP",
        description:
          "Speed ≠ sacrifice. On révolutionne le fast food avec des produits qui respectent la tradition coréenne. 3 minutes pour un vrai bol de Seoul, c'est possible ET c'est notre promesse.",
      },
      {
        emoji: "🌍",
        title: "SEOUL TO THE WORLD",
        description:
          "Mission : démocratiser la vraie culture food coréenne. Partout. Chaque bol Korean Street est un passeport direct pour Seoul, sans visa requis.",
      },
      {
        emoji: "💚",
        title: "RESPECT THE CRAFT",
        description:
          "On innove, mais toujours en honorant l'héritage culinaire de la Corée.",
      },
      {
        emoji: "🚀",
        title: "OBSESSED WITH BETTER",
        description:
          "Amélioration continue. Chaque recette, chaque détail peut être optimisé. L'excellence n'est pas un accident, c'est une obsession quotidienne.",
      },
      {
        emoji: "💥",
        title: "COMMUNITY DRIVEN",
        description:
          "Nos fans ne sont pas des clients, ce sont des ambassadeurs de Seoul. Leurs feedbacks nous font évoluer, leurs passions nous inspirent, leur énergie nous pousse vers l'avant.",
      },
      {
        emoji: "🎨",
        title: "CREATIVITY UNLEASHED",
        description:
          "Tradition + Innovation = Magic Korean Street. On respecte les codes tout en les réinventant. Spicy Carbonara ? Personne ne l'avait vu venir. Et c'est exactement ça, l'esprit Korean Street.",
      },
    ],
    footer: "No compromise. Just Seoul spirit. ✨",
  },
};

export default function OurValues() {
  const { language } = useLanguage();
  const { setActiveNavPanel } = useProduct();
  const content = valuesContent[language];

  return (
    <div className="min-h-full bg-red-600 relative">
      {/* Close Button - Mobile Only */}
      <button
        onClick={() => setActiveNavPanel(null)}
        className="lg:hidden absolute top-4 right-4 z-50"
      >
        <X className="w-8 h-8 text-white" />
      </button>

      {/* Title */}
      <div className="px-4 md:px-6 pt-6 md:pt-8 pb-4">
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight leading-tight">
          {language === "en" ? "OUR" : "NOS"}
        </h1>
        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tight leading-tight">
          {language === "en" ? "VALUES" : "VALEURS"}
        </h1>
      </div>

      {/* Header Image */}
      <div className="w-full px-4 md:px-6 py-4 md:py-6">
        <div className="relative w-full aspect-square md:aspect-auto overflow-hidden rounded-2xl">
          <img
            src={ourValuesPhoto}
            alt={language === "en" ? "Our Values" : "Nos Valeurs"}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Values Section - Red Background */}
      <div className="px-4 md:px-6 pb-4 md:pb-6">
        <div className="max-w-5xl mx-auto space-y-4">
          {/* Values - One per line */}
          {content.values.map((value, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-5 md:p-6 shadow-lg"
            >
              <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3">
                {value.emoji} {value.title}
              </h3>
              <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                {value.description}
              </p>
            </div>
          ))}

          {/* Footer */}
          <div className="text-center pt-6 pb-20 lg:pb-8">
            <p className="text-xl md:text-2xl font-bold text-white">
              {content.footer}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
